import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, ROLES } from "../lib/auth";
import {
  ChevronDown,
  LogOut,
  ShieldCheck,
  KeyRound,
  HelpCircle,
} from "lucide-react";

export default function UserMenu({ onStartTour, darkTopbar = false }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return null;
  const initials = user.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
  const roleDisplay = user.role_display || ROLES[user.role] || user.role;

  return (
    <div className="relative" ref={ref} data-testid="user-menu">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition ${darkTopbar ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
        data-testid="user-menu-button"
      >
        <div className="w-9 h-9 rounded-full bg-[#E0E621] text-[#0A3747] font-bold flex items-center justify-center text-sm">
          {initials}
        </div>
        <div className="hidden md:block text-left leading-tight">
          <div className={`text-sm font-medium ${darkTopbar ? "text-white" : "text-gray-900"}`}>{user.name.split(" ")[0]}</div>
          <div className={`text-[10px] uppercase tracking-wider font-mono ${darkTopbar ? "text-[#E0E621]" : "text-gray-500"}`}>{roleDisplay}</div>
        </div>
        <ChevronDown size={14} className={`${darkTopbar ? "text-gray-300" : "text-gray-400"} transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-lg shadow-xl z-[100] overflow-hidden user-menu-panel" data-testid="user-menu-dropdown">
          {/* Header */}
          <div className="user-menu-header px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full user-menu-avatar flex items-center justify-center">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="um-name text-sm truncate">{user.name}</div>
                <div className="um-email truncate">{user.email}</div>
              </div>
            </div>
            <div className="mt-2 um-badge">
              {roleDisplay}
            </div>
          </div>

          <div className="py-1">
            <MenuItem
              icon={ShieldCheck}
              label={user.mfa_enabled ? "MFA Activado" : "Activar MFA"}
              badge={user.mfa_enabled ? "ON" : null}
              onClick={() => { setOpen(false); navigate("/perfil"); }}
              testId="menu-mfa"
            />
            <MenuItem icon={KeyRound} label="Cambiar contraseña" onClick={() => { setOpen(false); navigate("/perfil"); }} />
            {onStartTour && (
              <MenuItem icon={HelpCircle} label="Recorrido del sistema" onClick={() => { setOpen(false); onStartTour(); }} testId="menu-tour" />
            )}
          </div>

          <div className="py-1" style={{ borderTop: "1px solid var(--border)" }}>
            <MenuItem icon={LogOut} label="Cerrar sesión" onClick={logout} danger testId="menu-logout" />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, badge, onClick, danger, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-4 py-2 text-sm submenu-item transition"
      style={danger ? { color: "#DC2626" } : undefined}
    >
      <Icon size={15} strokeWidth={1.6} />
      <span className="flex-1 text-left">{label}</span>
      {badge && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-green-100 text-green-700">{badge}</span>}
    </button>
  );
}
